import { PayloadAction, createSlice, current } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { Message, Updatable, WorkOrder } from 'shared';
import { SupportTicket } from 'ux/Customer/Support/types';
import { AIAssistantConversationAPI, AIAssistantMessageAPI } from 'ux/Tenant/AIAssistant/types';

import { RequestState } from '../../ds/types';

const initialState: AIAssistantState = {
  sidebarVisible: false,
  conversations: [],
  conversationInputs: [],
  pendingConversationMessages: [],
  supportTickets: [],
  inputFocusOrdinal: 0,
  newConversationRequestState: 'ready',
  conversationRequestStates: {}
};

export type AIAssistantState = {
  sidebarVisible: boolean;
  conversations: AIAssistantConversationAPI[];
  conversationInputs: AIAssistantConversationInput[];
  conversationRequestStates: { [conversationId: number]: RequestState };
  pendingConversationMessages: PendingConversationMessage[];
  supportTickets: SupportTicket[];
  inputFocusOrdinal: number;
  newConversationRequestState: RequestState;
};

export interface AIAssistantConversationInput {
  conversationId: number;
  input: string;
}

export interface PendingConversationMessage {
  conversationId: number;
  input: string;
}

const aiAssistantSlice = createSlice({
  name: 'AIAssistant',
  initialState,
  reducers: {
    setSidebarVisible: (state, { payload }: PayloadAction<boolean>) => {
      state.sidebarVisible = payload;
    },
    setConversations: (state, { payload }: PayloadAction<AIAssistantConversationAPI[]>) => {
      state.conversations = payload;
    },
    updateConversation: (state, { payload }: PayloadAction<{ conversation: AIAssistantConversationAPI }>) => {
      const conversationIndex = state.conversations.findIndex(
        conversation => conversation.id === payload.conversation.id
      );

      if (conversationIndex === -1) return;

      state.conversations[conversationIndex] = payload.conversation;
    },
    addMessage: (state, { payload }: PayloadAction<{ message: AIAssistantMessageAPI; conversationId: number }>) => {
      const conversation = state.conversations.find(conversation => conversation.id === payload.conversationId);

      if (!conversation) return;

      conversation.ai_assistant_messages.push(payload.message);
    },
    updateMessage: (
      state,
      {
        payload: { message, conversationId }
      }: PayloadAction<{ message: Updatable<AIAssistantMessageAPI>; conversationId: number }>
    ) => {
      const currentState = current(state);
      const conversationIndex = currentState.conversations.findIndex(
        conversation => conversation.id === conversationId
      );
      let conversation = currentState.conversations[conversationIndex];

      if (!conversation) return;

      const messageIndex = conversation.ai_assistant_messages.findIndex(m => m.id === message.id);

      const messageToUpdate = conversation.ai_assistant_messages[messageIndex];

      if (
        message.updated_at &&
        DateTime.fromISO(message.updated_at).toMillis() < DateTime.fromISO(messageToUpdate.updated_at).toMillis()
      ) {
        return;
      }
      const updatedMessage = { ...messageToUpdate, ...message };
      const updatedMessages = [...conversation.ai_assistant_messages];
      updatedMessages[messageIndex] = updatedMessage;

      state.conversations[conversationIndex] = { ...conversation, ai_assistant_messages: updatedMessages };
    },
    addConversation: (state, { payload }: PayloadAction<AIAssistantConversationAPI>) => {
      state.conversations.unshift(payload);
    },
    setConversationInput: (
      state,
      { payload: { conversationId, input } }: PayloadAction<AIAssistantConversationInput>
    ) => {
      const updatedInput = state.conversationInputs.find(input => input.conversationId === conversationId);

      if (updatedInput) {
        updatedInput.input = input;
      } else {
        state.conversationInputs.push({ conversationId, input });
      }
    },
    addPendingConversationMessage: (state, { payload }: PayloadAction<PendingConversationMessage>) => {
      state.pendingConversationMessages.push(payload);
    },
    removePendingConversationMessage: (state, { payload }: PayloadAction<{ conversationId: number }>) => {
      state.pendingConversationMessages = state.pendingConversationMessages.filter(
        ({ conversationId }) => conversationId !== payload.conversationId
      );
    },
    setSupportTickets: (state, { payload }: PayloadAction<SupportTicket[]>) => {
      state.supportTickets = payload;
    },
    updateSupportTicket: (state, { payload }: PayloadAction<SupportTicket>) => {
      const supportTicketIndex = state.supportTickets.findIndex(ticket => ticket.id === payload.id);

      if (supportTicketIndex === -1) {
        state.supportTickets.push(payload);
      } else {
        state.supportTickets[supportTicketIndex] = payload;
      }
    },
    addSupportTicketMessage: (state, { payload }: PayloadAction<{ supportTicketId: number; message: Message }>) => {
      const supportTicketIndex = state.supportTickets.findIndex(ticket => ticket.id === payload.supportTicketId);

      if (supportTicketIndex === -1) return;

      state.supportTickets[supportTicketIndex].messages.push(payload.message);
    },
    updateSupportTicketMessage: (state, { payload }: PayloadAction<Message>) => {
      const supportTicketId = state.supportTickets.find(t => t.messages.some(m => m.id === payload.id))?.id;

      if (!supportTicketId) return;

      const supportTicketIndex = state.supportTickets.findIndex(ticket => ticket.id === supportTicketId);

      const messageIndex = state.supportTickets[supportTicketIndex].messages.findIndex(m => m.id === payload.id);

      if (messageIndex === -1) return;

      state.supportTickets[supportTicketIndex].messages[messageIndex] = payload;
    },
    updateSupportTicketWorkOrder: (
      state,
      { payload }: PayloadAction<{ supportTicketId: number; workOrder: WorkOrder }>
    ) => {
      const supportTicketIndex = state.supportTickets.findIndex(ticket => ticket.id === payload.supportTicketId);

      if (supportTicketIndex === -1) return;

      const workOrderIndex = state.supportTickets[supportTicketIndex].work_orders.findIndex(
        workOrder => workOrder.id === payload.workOrder.id
      );

      if (workOrderIndex === -1) return;

      state.supportTickets[supportTicketIndex].work_orders[workOrderIndex] = payload.workOrder;
    },
    incrementInputFocusOrdinal: state => {
      state.inputFocusOrdinal += 1;
    },
    updateMessageFeedback: (state, { payload }: PayloadAction<{ messageId: number; conversationId: number }>) => {
      const currentState = current(state);

      const conversationIndex = currentState.conversations.findIndex(
        conversation => conversation.id === payload.conversationId
      );

      if (conversationIndex === -1) return;

      const messageIndex = currentState.conversations[conversationIndex].ai_assistant_messages.findIndex(
        message => message.id === payload.messageId
      );

      if (messageIndex === -1) return;

      state.conversations[conversationIndex].ai_assistant_messages[messageIndex].has_given_feedback = true;
    },
    setNewConversationRequestState: (state, { payload }: PayloadAction<RequestState>) => {
      state.newConversationRequestState = payload;
    },
    setRequestStateForConversation: (
      state,
      { payload }: PayloadAction<{ conversationId: number; requestState: RequestState }>
    ) => {
      state.conversationRequestStates[payload.conversationId] = payload.requestState;
    }
  }
});

export const { actions } = aiAssistantSlice;

export default aiAssistantSlice.reducer;
